import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { Checkbox } from "@workspace/web-ui/components/checkbox";
import { Label } from "@workspace/web-ui/components/label";
import { cn } from "@workspace/web-ui/lib/utils";

import { WelcomeFormSchema } from "../schema";
import { FormContainer, WelcomeStepFormProps } from "./form-container";
import { TextareaInput } from "./textarea-input";

export function EventsStep({ data, updateFields }: WelcomeStepFormProps) {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<WelcomeFormSchema>();

  const selectedEvents = watch("events");

  useEffect(() => {
    updateFields({
      events: selectedEvents,
    });
  }, [selectedEvents, updateFields]);

  const handleOtherChange = (value: string) => {
    setValue("otherEvents", value, {
      shouldValidate: true,
    });
    updateFields({ otherEvents: value });
  };

  return (
    <FormContainer heading={data.heading} description={data.description}>
      {errors.events && (
        <p className="my-2 text-right text-sm font-medium text-destructive sm:text-base">
          {errors.events?.message}
        </p>
      )}

      <Controller
        control={control}
        name="events"
        rules={{ required: "Please select at least one event" }}
        render={() => (
          <div className="w-full space-y-4">
            {data.form?.map((item) => (
              <Controller
                key={item.label}
                control={control}
                name="events"
                render={({ field }) => {
                  // Ensure field.value is always an array
                  const currentValue = Array.isArray(field.value)
                    ? field.value
                    : field.value
                      ? [field.value]
                      : [];

                  return (
                    <Label
                      htmlFor={item.label}
                      className={cn(
                        "flex w-full cursor-pointer flex-row-reverse items-center justify-between rounded-[16px] border-[1px] border-solid p-[16px] shadow-[0px_1px_3px_0px_#1D0D680D_0px_5px_5px_0px_#1D0D680A_0px_12px_7px_0px_#1D0D6808] transition-colors duration-300 ease-in-out",
                        "h-[60px] gap-[12px]",
                        "md:h-[70px] md:gap-[16px] md:p-[20px]",
                        "lg:h-[80px] lg:gap-[24px] lg:p-[24px]",
                        currentValue.includes(item.value as "Other" | "Wedding" | "Vacation" | "Reunion" | "Health Reasons")
                          ? "border-[#4272DD]"
                          : "border-[rgba(0,0,0,0.1)]",
                        errors.events?.message
                          ? "border-destructive"
                          : "",
                      )}
                    >
                      <Checkbox
                        id={item.label}
                        checked={currentValue.includes(item.value as "Other" | "Wedding" | "Vacation" | "Reunion" | "Health Reasons")}
                        onCheckedChange={(checked) => {
                          const newValue = checked
                            ? [...currentValue, item.value]
                            : currentValue.filter(
                                (value) => value !== item.value,
                              );

                          field.onChange(newValue);
                        }}
                      />
                      <p className="text-left text-base text-[#7F8493] transition-colors duration-200 ease-in-out peer-checked:text-[#363840] md:text-lg">
                        {item.label}
                      </p>
                    </Label>
                  );
                }}
              />
            ))}
          </div>
        )}
      />
      {selectedEvents && selectedEvents.includes("Other") && (
        <>
          <TextareaInput
            value={watch("otherEvents") || ""}
            onChange={handleOtherChange}
          />
        </>
      )}
    </FormContainer>
  );
}
